import { Component, inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { BannerService } from '@terminus-ui/banner';
import { ButtonModule } from 'primeng/button';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { firstValueFrom } from 'rxjs';
import { ApplicationManagerApiService } from '../application-manager-api.service';

@Component({
  providers: [DialogService],
  imports: [
    TableModule,
    ButtonModule,
    SelectModule,
    InputTextModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './customer-dialog.component.html',
})
export class CustomerDialogComponent implements OnInit {
  customerForm = new FormGroup({
    crmType: new FormControl(''),
    customerId: new FormControl(''),
    name: new FormControl(''),
    domain: new FormControl(''),
    contactEmail: new FormControl(''),
    status: new FormControl('ONBOARDING'),
    owner: new FormControl(''),
    internalCrmId: new FormControl(null),
    createdDate: new FormControl(null),
  });
  isEdit = false;
  protected CRMOptions = ['SALESFORCE', 'DYNAMICS', 'HUBSPOT', 'OTHER'];
  protected StatusOptions = [
    'ONBOARDING',
    'ACTIVE',
    'PROSPECT',
    'SUSPENDED',
    'DECOMMISSIONED',
  ];
  private ref = inject(DynamicDialogRef);
  private bannerService = inject(BannerService);
  private applicationManagerApi = inject(ApplicationManagerApiService);
  private dialogConfig = inject(DynamicDialogConfig);

  ngOnInit(): void {
    if (this.dialogConfig.data?.customer) {
      firstValueFrom(this.dialogConfig.data.customer).then((customer) => {
        this.customerForm.patchValue(
          customer as {
            crmType: string | null;
            customerId: string | null;
            name: string | null;
            domain: string | null;
            contactEmail: string | null;
            status: string | null;
            owner: string | null;
          },
        );

        this.customerForm.controls.crmType.disable();
        this.customerForm.controls.internalCrmId.disable();
        this.customerForm.controls.domain.disable();
        this.customerForm.controls.name.disable();
      });
      this.isEdit = true;
    } else {
      this.customerForm.reset();
      this.isEdit = false;
    }
  }

  /** Attempt adding a customer, closes dialog after */
  provisionCustomer() {
    this.applicationManagerApi
      .createCustomer({
        customer: {
          contactEmail: this.customerForm.get('contactEmail')?.value || '',
          crmType: this.customerForm.get('crmType')?.value || '',
          internalCrmId: this.customerForm.get('internalCrmId')?.value || '',
          domain: this.customerForm.get('domain')?.value || '',
          name: this.customerForm.get('name')?.value || '',
          owner: this.customerForm.get('owner')?.value || '',
          status: this.customerForm.get('status')?.value || 'ONBOARDING',
        },
      })
      .subscribe({
        next: () => {
          this.bannerService.pushBanner({
            isClosable: true,
            isFloating: false,
            title: `Successfully provisioned a new customer.`,
            type: 'success',
            enableAutoDismissWithDuration: 10000,
          });
          this.customerForm.reset();
          this.ref.close(true);
        },
        error: (err) => {
          this.bannerService.pushBanner({
            isClosable: true,
            isFloating: false,
            title: `Unable to provision a new customer.`,
            type: 'warning',
            enableAutoDismissWithDuration: 10000,
          });
          console.warn('Unable to provision customer:', err);
        },
      });
  }

  editCustomer() {
    this.applicationManagerApi
      .editCustomer({
        customer: {
          customerId: this.customerForm.get('customerId')?.value || '',
          contactEmail: this.customerForm.get('contactEmail')?.value || '',
          internalCrmId: this.customerForm.get('internalCrmId')?.value || '',
          name: this.customerForm.get('name')?.value || '',
          owner: this.customerForm.get('owner')?.value || '',
          status: this.customerForm.get('status')?.value || 'ONBOARDING',
        },
      })
      .subscribe({
        next: () => {
          this.bannerService.pushBanner({
            isClosable: true,
            isFloating: false,
            title: `Successfully edited customer.`,
            type: 'success',
            enableAutoDismissWithDuration: 10000,
          });
          this.customerForm.reset();
          this.ref.close(true);
        },
        error: (err) => {
          this.bannerService.pushBanner({
            isClosable: true,
            isFloating: false,
            title: `Unable to edit customer.`,
            type: 'warning',
            enableAutoDismissWithDuration: 10000,
          });
          console.warn('Unable to edit customer:', err);
        },
      });
  }

  cancel() {
    this.customerForm.reset();
    this.ref.close(false);
  }
}
